<template>
  <div class="notice-detail-wrap">
    <!-- 面包屑导航 -->
    <div class="breadcrumbBar">
      <div class="breadcrumbContent">
        <span class="breadcrumbLink" @click="$router.push('/Home')">首页</span>
        <span class="breadcrumbSeparator">></span>
        <span class="breadcrumbCurrent">通知公告</span>
      </div>
    </div>

    <div class="notice-detail-body">
      <!-- 详情图片模式：多图依次垂直排列 -->
      <div v-if="noticeInfo && detailImageList.length > 0" class="notice-detail-image-wrapper">
        <div class="image-list">
          <div
            v-for="(img, idx) in detailImageList"
            :key="idx"
            class="image-list-item"
            :class="{ 'is-last': idx === detailImageList.length - 1 && noticeInfo.linkUrl }"
            @click="handleImageClick(idx)"
          >
            <img
              :src="img"
              :loading="idx === 0 ? 'eager' : 'lazy'"
              :fetchpriority="idx === 0 ? 'high' : 'low'"
              decoding="async"
              class="notice-detail-image"
              alt="详情图片"
            />
            <div v-if="idx === detailImageList.length - 1 && noticeInfo.linkUrl" class="image-link-hint">
              点击查看更多
            </div>
          </div>
        </div>
      </div>

      <!-- 文字内容模式：原样式展示 -->
      <div v-else-if="noticeInfo" class="notice-detail">
        <h1 class="notice-detail__title">{{ noticeInfo.title }}</h1>
        <div class="notice-detail__meta">
          <span class="notice-detail__time">{{ noticeInfo.creatorTime | dateFormat }}</span>
        </div>
        <div class="notice-detail__divider"></div>
        <div class="notice-detail__content" ref="noticeRichContent" v-html="noticeInfo.content"></div>
      </div>

      <div v-else-if="loading" class="notice-detail__loading">
        <i class="el-icon-loading"></i> 加载中...
      </div>
      <div v-else class="notice-detail__empty">暂无内容</div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import define from "@/utils/define";

const baseUrl = define.APIURl;

export default {
  name: "NoticeDetail",
  filters: {
    dateFormat(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },
  data() {
    return {
      noticeInfo: null,
      loading: false,
    };
  },
  computed: {
    detailImageList() {
      const info = this.noticeInfo;
      if (!info || !info.detailImgUrl) return [];
      const raw = info.detailImgUrl;
      try {
        const parsed = JSON.parse(raw);
        const items = Array.isArray(parsed) ? parsed : [parsed];
        return items
          .map((item) => {
            const url = item?.url || item;
            if (!url) return null;
            return /^https?:\/\//.test(url) ? url : baseUrl + url;
          })
          .filter(Boolean);
      } catch (e) {
        if (raw.startsWith('http://') || raw.startsWith('https://')) return [raw];
        if (raw.startsWith('/')) return [baseUrl + raw];
        return [];
      }
    },
  },
  mounted() {
    const id = this.$route.query.id;
    if (id) {
      this.fetchDetail(id);
    }
  },
  methods: {
    fetchDetail(id) {
      this.loading = true;
      request({ url: `/api/business/baseBizNotice/getInfo/${id}`, method: "get" })
        .then((res) => {
          this.noticeInfo = res.data || null;
          this.$nextTick(() => {
            this.enhanceRichTextImages();
          });
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
    enhanceRichTextImages() {
      const container = this.$refs.noticeRichContent;
      if (!container) return;

      const images = container.querySelectorAll("img");
      images.forEach((img) => {
        if (!img.getAttribute("loading")) {
          img.setAttribute("loading", "lazy");
        }
        if (!img.getAttribute("decoding")) {
          img.setAttribute("decoding", "async");
        }
        if (!img.getAttribute("fetchpriority")) {
          img.setAttribute("fetchpriority", "low");
        }
        img.style.maxWidth = "100%";
        img.style.height = "auto";
        img.style.objectFit = "contain";
      });
    },
    handleImageClick(idx) {
      const isLast = idx === this.detailImageList.length - 1;
      const linkUrl = this.noticeInfo?.linkUrl;
      if (isLast && linkUrl) {
        window.open(linkUrl, '_blank');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.notice-detail-wrap {
  min-height: calc(100vh - 140px);
  background: #f7f8fc;
}

/* 面包屑导航 */
.breadcrumbBar {
  background: #333;
  padding: 16px 0;

  .breadcrumbContent {
    width: 1200px;
    margin: 0 auto;
    font-size: 14px;
    color: #fff;

    .breadcrumbLink {
      color: #aaa;
      cursor: pointer;

      &:hover {
        color: #fff;
      }
    }

    .breadcrumbSeparator {
      margin: 0 8px;
      color: #666;
    }

    .breadcrumbCurrent {
      color: #fff;
    }
  }
}

.notice-detail-body {
  padding: 0;
  min-height: calc(100vh - 140px - 48px);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.notice-detail-image-wrapper {
  width: 100%;
  max-width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-list-item {
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;

  &.is-last {
    cursor: pointer;
  }
}

.notice-detail-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.image-link-hint {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  pointer-events: none;
}

.notice-detail {
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  padding: 40px 48px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  &__title {
    font-size: 26px;
    font-weight: 700;
    color: #1a1a2e;
    line-height: 1.5;
    margin: 0 0 16px;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
  }

  &__time {
    font-size: 13px;
    color: #999;
  }

  &__divider {
    height: 1px;
    background: #eef0f5;
    margin-bottom: 28px;
  }

  &__content {
    font-size: 15px;
    color: #333;
    line-height: 1.9;

    ::v-deep img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
    }

    ::v-deep p {
      margin: 0 0 12px;
    }

    ::v-deep a {
      color: #2b85e5;
      text-decoration: none;

      &:hover { text-decoration: underline; }
    }
  }

  &__loading,
  &__empty {
    text-align: center;
    color: #999;
    padding: 80px 0;
    font-size: 15px;
  }
}
</style>
